import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'

interface ConnectivityState {
  isOnline: boolean
  lastOnlineAt: number | null
  wentOfflineAt: number | null
}

const ConnectivityContext = createContext<ConnectivityState>({ isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true, lastOnlineAt: null, wentOfflineAt: null })

export function useConnectivity() {
  return useContext(ConnectivityContext)
}

export function ConnectivityProvider({ children }: { children: React.ReactNode }) {
  const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true)
  const lastOnlineRef = useRef<number | null>(isOnline ? Date.now() : null)
  const wentOfflineRef = useRef<number | null>(!isOnline ? Date.now() : null)

  useEffect(() => {
    function handleOnline() {
      lastOnlineRef.current = Date.now()
      try { Sentry.addBreadcrumb({ category: 'connectivity', message: 'online', level: 'info' }) } catch {}
      setIsOnline(true)
    }
    function handleOffline() {
      wentOfflineRef.current = Date.now()
      try { Sentry.addBreadcrumb({ category: 'connectivity', message: 'offline', level: 'warning' }) } catch {}
      setIsOnline(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const value = useMemo(() => ({
    isOnline,
    lastOnlineAt: lastOnlineRef.current,
    wentOfflineAt: wentOfflineRef.current,
  }), [isOnline])

  return (
    <ConnectivityContext.Provider value={value}>{children}</ConnectivityContext.Provider>
  )
}
